import { useState, useEffect } from 'react'
import { getBelt, makeAvatarSvg, makeInitialsSvg, getFlagSvg } from '../lib/helpers'
import { FRONT_STAT_KEYS } from '../lib/constants'

export default function BeltPromotion({ fighter, newBeltIndex, onClose }) {
  const [stage, setStage] = useState('reveal') // 'reveal' | 'card' | 'share'
  const [confetti, setConfetti] = useState([])

  const oldBelts = require('../lib/constants')[fighter.type === 'kids' ? 'KIDS_BELTS' : 'ADULT_BELTS']
  const newBelt = oldBelts[newBeltIndex]
  const theme = BELT_THEMES[newBelt?.th] || BELT_THEMES.wht

  // Generate confetti pieces
  useEffect(() => {
    const pieces = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 2 + Math.random() * 2,
      color: ['#d4a017', '#1860a8', '#c8102e', '#169b62', '#7060d0'][Math.floor(Math.random() * 5)],
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
    }))
    setConfetti(pieces)
  }, [])

  const faceContent = fighter.photo_url
    ? `<img src="${fighter.photo_url}" style="width:100%;height:100%;object-fit:cover;object-position:top center;display:block" />`
    : fighter.avatar_style === 'initials'
      ? makeInitialsSvg(fighter.first_name, fighter.last_name, newBelt?.a1 || '#eee', newBelt?.a2 || '#aaa', 160)
      : makeAvatarSvg(fighter.avatar_seed || 1, newBelt?.a1 || '#eee', newBelt?.a2 || '#aaa', 160)

  const flagSvg = getFlagSvg(fighter.country || 'HR')

  function copyShareText() {
    const text = `🥋 ${fighter.first_name} ${fighter.last_name} just got promoted to ${newBelt?.n} belt! #BJJ #FighterCards`
    navigator.clipboard.writeText(text).then(() => alert('Copied to clipboard!'))
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', overflow: 'hidden'
    }}>
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes cardReveal {
          0% { transform: scale(0.3) rotateY(180deg); opacity: 0; }
          60% { transform: scale(1.08) rotateY(0deg); opacity: 1; }
          100% { transform: scale(1) rotateY(0deg); opacity: 1; }
        }
        @keyframes shine { 0%{left:-80%} 60%{left:130%} 100%{left:130%} }
        @keyframes fadeUp { from { opacity:0; transform: translateY(20px); } to { opacity:1; transform:none; } }
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
      `}</style>

      {/* Confetti */}
      {confetti.map(p => (
        <div key={p.id} style={{
          position: 'absolute', left: `${p.x}%`, top: -10,
          width: p.size, height: p.size,
          background: p.color, borderRadius: p.size * 0.2,
          animation: `confettiFall ${p.duration}s ${p.delay}s ease-in infinite`,
          transform: `rotate(${p.rotation}deg)`,
          pointerEvents: 'none', zIndex: 1
        }} />
      ))}

      {/* Main content */}
      <div style={{ textAlign: 'center', zIndex: 2, maxWidth: 360, width: '100%' }}>
        {/* Header */}
        <div style={{ animation: 'fadeUp 0.5s ease both', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>🏆</div>
          <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            Belt Promotion!
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0 }}>
            {fighter.first_name} {fighter.last_name} has been promoted to
          </p>
          <div style={{
            display: 'inline-block', marginTop: 10,
            padding: '6px 20px', borderRadius: 30,
            background: theme.bg, border: `2px solid ${theme.border}`,
            color: theme.color, fontWeight: 700, fontSize: 16,
            animation: 'pulse 2s ease infinite'
          }}>
            {newBelt?.n} Belt
          </div>
        </div>

        {/* Promoted card */}
        <div style={{
          animation: 'cardReveal 0.8s 0.3s cubic-bezier(0.34,1.56,0.64,1) both',
          display: 'inline-block', marginBottom: '1.5rem'
        }}>
          <div style={{
            width: 200, height: 300, borderRadius: 14, overflow: 'hidden',
            background: theme.bg, border: `2.5px solid ${theme.border}`,
            color: theme.color, position: 'relative',
            boxShadow: `0 0 40px ${theme.border}80`
          }}>
            {/* Shine */}
            <div style={{
              position: 'absolute', top: 0, left: '-80%', width: '40%', height: '100%',
              background: 'linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.3) 50%,transparent 70%)',
              animation: 'shine 2s 1s infinite', pointerEvents: 'none', zIndex: 10
            }} />

            {/* Face */}
            <div style={{ position: 'relative', height: '62%', overflow: 'hidden' }}>
              <div dangerouslySetInnerHTML={{ __html: faceContent }} style={{ width: '100%', height: '100%' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom,transparent 50%,rgba(0,0,0,0.35) 100%)' }} />
              {/* Rating badge */}
              <div style={{ position: 'absolute', top: 8, left: 8, lineHeight: 1 }}>
                <div style={{ fontSize: 36, fontWeight: 700, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>🆙</div>
              </div>
              {/* Flag */}
              <div style={{ position: 'absolute', top: 8, right: 8 }}>
                <div style={{ width: 24, height: 16, borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.15)' }}
                  dangerouslySetInnerHTML={{ __html: flagSvg }} />
              </div>
            </div>

            {/* Bottom */}
            <div style={{ padding: '8px 10px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, textAlign: 'center', letterSpacing: '0.06em', marginBottom: 4 }}>
                {fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}
              </div>
              <div style={{ height: '0.5px', background: 'currentColor', opacity: 0.25, marginBottom: 6 }} />
              <div style={{ textAlign: 'center', fontSize: 11, opacity: 0.85, fontWeight: 600 }}>
                {newBelt?.n} Belt
              </div>
              <div style={{ textAlign: 'center', fontSize: 10, opacity: 0.6, marginTop: 3 }}>
                {fighter.type === 'kids' ? 'Kids' : 'Adult'} · {fighter.sessions_count} sessions
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{
          animation: 'fadeUp 0.5s 0.9s ease both',
          display: 'flex', flexDirection: 'column', gap: 10
        }}>
          <button onClick={copyShareText} style={{
            width: '100%', padding: '12px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg,#1860a8,#0c4480)',
            color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            Share promotion
          </button>
          <button onClick={onClose} style={{
            width: '100%', padding: '11px', borderRadius: 10,
            background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
            color: 'rgba(255,255,255,0.8)', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit'
          }}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
